import { type ComponentProps, type ReactNode } from "react";

import { PinIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Label } from "simplydo/react/ui/label";

export const FeedItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  min-width: 0;
`;

const FeedItemIconCell = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 34px;
  height: 34px;
  padding-top: 4px;
  padding-left: 4px;
  flex-shrink: 0;
`;

const FeedItemIconPins = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  line-height: 0;
  pointer-events: none;

  ${Label} {
    font-size: 0.5em;
  }

  i {
    margin: 0 !important;
  }

  > * {
    pointer-events: auto;
  }
`;

const FeedItemIconImage = styled(Image)`
  width: 30px;
  min-width: 30px;
  height: 30px;
  border-radius: 8px;
  object-fit: cover;
  background-color: ${({ theme }) => theme.primaryColour} !important;
  flex-shrink: 0;
`;

type FeedItemIconProps = ComponentProps<typeof FeedItemIconImage> & {
  pinned?: boolean;
};

export const FeedItemIcon = ({ pinned, ...props }: FeedItemIconProps) => (
  <FeedItemIconCell>
    <FeedItemIconImage {...props} />
    {pinned ? (
      <FeedItemIconPins>
        <Label color="green" size="small" circular>
          <Icon icon={PinIcon} />
        </Label>
      </FeedItemIconPins>
    ) : null}
  </FeedItemIconCell>
);

const FeedItemIconCircleInner = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.primary.soft};
  flex-shrink: 0;
`;

type FeedItemIconCircleProps = { children?: ReactNode };

export const FeedItemIconCircle = ({ children }: FeedItemIconCircleProps) => (
  <FeedItemIconCell>
    <FeedItemIconCircleInner>{children}</FeedItemIconCircleInner>
  </FeedItemIconCell>
);

export const FeedItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;

  img,
  video,
  iframe,
  table,
  pre {
    max-width: 100%;
  }

  pre {
    overflow-x: auto;
  }
`;

export const FeedItemMessage = styled.div`
  line-height: 1.3em;
  color: #999;

  .chip {
    color: #000 !important;
    font-size: 0.95em;
  }
`;

export const FeedItemDate = styled.div`
  font-size: 0.85em;
  color: #999;
`;

export const FeedItemActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
`;
